import { Button } from '@/components/common/button'
import { Popover, PopoverContent, PopoverList, PopoverListItem, PopoverTrigger } from '@/components/common/popover'
import { SortIcon } from '@/components/icons/sort'
import { useMixpanel } from '@/context/mixpanel'
import { type FixedTermSortBy, useFixedTermMarketsSearchState } from '@/hooks/useFixedTermMarketsSearchState'
import { cn } from '@/utils/cn'
import type { PopoverProps } from '@radix-ui/react-popover'

const SORT_OPTIONS: FixedTermSortBy[] = ['expiry', 'tvl', 'fixedApy']

interface FixedTermMarketsSortSelectProps {
  className?: string
}

export const FixedTermMarketsSortSelect = ({
  className,
  modal,
}: FixedTermMarketsSortSelectProps & Pick<PopoverProps, 'modal'>) => {
  const { sortBy, sortOrder, setSortBy } = useFixedTermMarketsSearchState()
  const { trackEvent } = useMixpanel()

  return (
    <Popover modal={modal}>
      <PopoverTrigger asChild>
        <Button
          variant="default"
          size="md"
          className={cn(
            'group flex items-center gap-2 rounded-xl border bg-surface px-3 text-left transition-colors',
            'hocus:border-accent hocus:text-body-foreground',
            className
          )}
        >
          <SortIcon size={16} className={cn('transition-transform', sortOrder === 'asc' && '-scale-y-100')} />
          <span>Sort by {getSortLabel(sortBy)}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent>
        <PopoverList>
          {SORT_OPTIONS.map((option) => (
            <PopoverListItem key={option} className="h-10 border-t">
              <Button
                variant="default"
                onClick={() => {
                  setSortBy(option)
                  trackEvent('Fixed term markets sort selected', {
                    sortBy: option,
                    previousSortBy: sortBy,
                    sortOrder,
                    Page: 'Discover',
                    'Opportunity type': 'Fixed term',
                  })
                }}
                className="flex h-full w-full max-w-full justify-between truncate hocus-within:bg-surface px-4"
              >
                <div className="flex w-full max-w-full items-center justify-between gap-2 truncate">
                  <span className="first-letter:uppercase">{getSortLabel(option)}</span>
                  <SortIcon
                    className={cn(
                      'transition-transform',
                      option === sortBy ? 'opacity-100' : 'opacity-0',
                      sortOrder === 'asc' && '-scale-y-100'
                    )}
                  />
                </div>
              </Button>
            </PopoverListItem>
          ))}
        </PopoverList>
      </PopoverContent>
    </Popover>
  )
}

const getSortLabel = (sortBy: FixedTermSortBy) => {
  switch (sortBy) {
    case 'expiry':
      return 'time to maturity'
    case 'fixedApy':
      return 'fixed APY'
    case 'tvl':
      return 'TVL'
  }
}
